/**
 * SuperAdminSection - Staff management for super admin
 *
 * Simple interface to:
 * - Create admins, workers, and referees
 * - Deactivate/reactivate staff members
 */

import { useState, useCallback, useEffect } from 'react';
import { RefreshCw, UserPlus, UserMinus, UserCheck, Shield, Briefcase, Users } from 'lucide-react';
import { StaffLayout } from '@/components/layout';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

interface StaffMember {
  identifier: string;
  telegramId?: string | null;
  role: 'super_admin' | 'admin' | 'worker' | 'referee';
  active: boolean;
  createdAt: string;
  lastActiveDate: string | null;
  name?: string | null;
  referralCode?: string | null;
  balance?: number | null;
}

type ManagedRole = 'admin' | 'worker' | 'referee';

export interface SuperAdminSectionProps {
  staff: StaffMember[];
  isLoading?: boolean;
  onRefresh?: () => void;
  onCreateStaff?: (telegramId: string, role: ManagedRole, name?: string, referralCode?: string) => Promise<{ success: boolean; error?: string }>;
  onDeactivateStaff?: (identifier: string) => Promise<{ success: boolean; error?: string }>;
  onReactivateStaff?: (identifier: string) => Promise<{ success: boolean; error?: string }>;
}

// =============================================================================
// ROLE CONFIG
// =============================================================================

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', icon: Shield, color: 'text-purple-600 bg-purple-100' },
  admin: { label: 'Admin', icon: Shield, color: 'text-blue-600 bg-blue-100' },
  worker: { label: 'Worker', icon: Briefcase, color: 'text-green-600 bg-green-100' },
  referee: { label: 'Referee', icon: Users, color: 'text-orange-600 bg-orange-100' },
} as const;

// =============================================================================
// COMPONENT
// =============================================================================

export function SuperAdminSection({
  staff,
  isLoading = false,
  onRefresh,
  onCreateStaff,
  onDeactivateStaff,
  onReactivateStaff,
}: SuperAdminSectionProps) {
  // Form state
  const [telegramId, setTelegramId] = useState('');
  const [selectedRole, setSelectedRole] = useState<ManagedRole>('admin');
  const [refereeName, setRefereeName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  // Action states
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Handle create
  const handleCreate = useCallback(async () => {
    if (!telegramId.trim() || !onCreateStaff) return;

    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      const result = await onCreateStaff(
        telegramId.trim(),
        selectedRole,
        selectedRole === 'referee' ? refereeName.trim() || undefined : undefined,
        selectedRole === 'referee' ? referralCode.trim() || undefined : undefined
      );

      if (result.success) {
        setCreateSuccess(`${selectedRole} created successfully`);
        setTelegramId('');
        setRefereeName('');
        setReferralCode('');
        onRefresh?.();
      } else {
        setCreateError(result.error || 'Failed to create staff');
      }
    } catch (error) {
      setCreateError('Failed to create staff');
    } finally {
      setIsCreating(false);
    }
  }, [telegramId, selectedRole, refereeName, referralCode, onCreateStaff, onRefresh]);

  // Handle deactivate
  const handleDeactivate = useCallback(async (identifier: string) => {
    if (!onDeactivateStaff) return;

    setActionInProgress(identifier);
    try {
      await onDeactivateStaff(identifier);
      onRefresh?.();
    } finally {
      setActionInProgress(null);
    }
  }, [onDeactivateStaff, onRefresh]);

  // Handle reactivate
  const handleReactivate = useCallback(async (identifier: string) => {
    if (!onReactivateStaff) return;

    setActionInProgress(identifier);
    try {
      await onReactivateStaff(identifier);
      onRefresh?.();
    } finally {
      setActionInProgress(null);
    }
  }, [onReactivateStaff, onRefresh]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (createError || createSuccess) {
      const timer = setTimeout(() => {
        setCreateError(null);
        setCreateSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [createError, createSuccess]);

  // Group staff by role
  const groupedStaff = staff.reduce<Record<string, StaffMember[]>>((acc, member) => {
    const role = member.role;
    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role]!.push(member);
    return acc;
  }, {});

  return (
    <StaffLayout>
      <div className="flex flex-col h-full bg-slate-100">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Staff Management</h1>
              <p className="text-sm text-slate-500">
                {staff.length} staff member{staff.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className={cn(
                'p-2.5 rounded-full',
                'bg-white border border-slate-200',
                'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                'transition-all',
                isLoading && 'opacity-50',
              )}
            >
              <RefreshCw className={cn('w-5 h-5 text-slate-600', isLoading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* Create Staff Form */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Create Staff Member
            </h2>

            {/* Telegram ID */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Telegram ID
              </label>
              <input
                type="text"
                value={telegramId}
                onChange={(e) => setTelegramId(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter Telegram ID"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Role Selection */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Role
              </label>
              <div className="flex gap-2">
                {(['admin', 'worker', 'referee'] as ManagedRole[]).map((role) => {
                  const config = ROLE_CONFIG[role];
                  const Icon = config.icon;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setSelectedRole(role)}
                      className={cn(
                        'flex-1 py-2 px-3 rounded-lg border-2 transition-all',
                        'flex items-center justify-center gap-2',
                        selectedRole === role
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300',
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Referee-specific fields */}
            {selectedRole === 'referee' && (
              <div className="space-y-3 mb-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name (optional)
                  </label>
                  <input
                    type="text"
                    value={refereeName}
                    onChange={(e) => setRefereeName(e.target.value)}
                    placeholder="Display name for referee"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Referral Code (optional)
                  </label>
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Custom code (auto-generated if empty)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            )}

            {/* Error/Success Messages */}
            {createError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {createError}
              </div>
            )}
            {createSuccess && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {createSuccess}
              </div>
            )}

            {/* Create Button */}
            <button
              type="button"
              onClick={handleCreate}
              disabled={!telegramId.trim() || isCreating}
              className={cn(
                'w-full py-3 rounded-lg font-semibold text-white transition-all',
                'bg-blue-600 hover:bg-blue-700',
                (!telegramId.trim() || isCreating) && 'opacity-50 cursor-not-allowed',
              )}
            >
              {isCreating ? 'Creating...' : `Create ${ROLE_CONFIG[selectedRole].label}`}
            </button>
          </div>

          {/* Staff List */}
          <div className="space-y-4">
            {(['super_admin', 'admin', 'worker', 'referee'] as const).map((role) => {
              const members = groupedStaff[role] || [];
              if (members.length === 0) return null;

              const config = ROLE_CONFIG[role];
              const Icon = config.icon;

              return (
                <div key={role} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className={cn('px-4 py-2 flex items-center gap-2', config.color)}>
                    <Icon className="w-4 h-4" />
                    <span className="font-semibold">{config.label}s ({members.length})</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {members.map((member) => (
                      <div
                        key={member.identifier}
                        className={cn(
                          'px-4 py-3 flex items-center justify-between',
                          !member.active && 'bg-slate-50 opacity-60',
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-slate-600">
                              {member.telegramId || member.identifier}
                            </span>
                            {!member.active && (
                              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          {member.name && (
                            <div className="text-sm text-slate-500">{member.name}</div>
                          )}
                          {member.referralCode && (
                            <div className="text-xs text-orange-600">Code: {member.referralCode}</div>
                          )}
                        </div>

                        {/* Action button (not for super_admin) */}
                        {role !== 'super_admin' && (
                          <button
                            type="button"
                            onClick={() => member.active
                              ? handleDeactivate(member.identifier)
                              : handleReactivate(member.identifier)
                            }
                            disabled={actionInProgress === member.identifier}
                            className={cn(
                              'p-2 rounded-lg transition-all',
                              member.active
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'bg-green-100 text-green-600 hover:bg-green-200',
                              actionInProgress === member.identifier && 'opacity-50',
                            )}
                          >
                            {member.active ? (
                              <UserMinus className="w-4 h-4" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty state */}
          {staff.length === 0 && !isLoading && (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No staff members yet</p>
            </div>
          )}
        </div>
      </div>
    </StaffLayout>
  );
}
